import type { ReactNode } from "react";
import { Navigate, type RouteObject } from "react-router-dom";
import { useNewLook } from "@/hooks/use-new-look";
import { AllIdeasPage } from "@/pages/ideas/all-ideas";
import { NewIdeaPage } from "@/pages/ideas/new-idea";
import { IdeaPage } from "@/pages/ideas/idea-page";
import { EpicRequestsPage } from "@/pages/epic-requests/index";
import { EpicRequestDetailPage } from "@/pages/epic-requests/epic-request-detail";
import { EpicsPage } from "@/pages/epics/index";
import { AllBlueprintsPage } from "@/pages/blueprints/all-blueprints";
import { BlueprintPage } from "@/pages/blueprints/blueprint-page";
import { EpicDetailPage } from "@/pages/epics/epic-detail";
import { SettingsPage } from "@/pages/settings";
import { NewSettingsPage } from "@/pages/new-settings/settings";
import { DashboardPage } from "@/pages/dashboard";
import { HomeDashboardPage } from "@/pages/home/home-dashboard";
import { TeamRollupPage } from "@/pages/team-rollup";
import { TeamBoardPage } from "@/pages/team/team-board";
import { TeamsPage } from "@/pages/teams/index";
import { AllTeamsPage } from "@/pages/teams/all-teams";

/** Shows the rebuilt page while "Try the new look" is on, today's page otherwise. */
export function ByLook({ classic, next }: { classic: ReactNode; next: ReactNode }) {
  const [newLook] = useNewLook();
  return <>{newLook ? next : classic}</>;
}

const ideasPage = <ByLook classic={<EpicRequestsPage />} next={<AllIdeasPage />} />;
const blueprintsPage = <ByLook classic={<EpicsPage />} next={<AllBlueprintsPage />} />;

/**
 * The new look's addresses (docs/new-look/PLAN.md, step 1.4). They show
 * today's pages until each is rebuilt; the old /epic-requests and /epics
 * addresses keep working and show the same pages.
 */
export const newLookRoutes: RouteObject[] = [
  { path: "ideas", element: ideasPage },
  // The new look's composer; the classic look keeps its New Request dialog.
  {
    path: "ideas/new",
    element: <ByLook classic={<Navigate to="/epic-requests" replace />} next={<NewIdeaPage />} />,
  },
  // The rebuilt idea page; /epic-requests/:id stays the classic page either way.
  {
    path: "ideas/:requestId",
    element: <ByLook classic={<EpicRequestDetailPage />} next={<IdeaPage />} />,
  },
  { path: "blueprints", element: blueprintsPage },
  // The rebuilt blueprint page; /epics/:id stays the classic page either way.
  { path: "blueprints/:epicId", element: <ByLook classic={<EpicDetailPage />} next={<BlueprintPage />} /> },
];

/** Old addresses that now show a rebuilt page while the new look is on. */
export const rebuiltOldRoutes: RouteObject[] = [
  { path: "settings", element: <ByLook classic={<SettingsPage />} next={<NewSettingsPage />} /> },
  {
    path: "dashboard",
    element: <ByLook classic={<DashboardPage />} next={<HomeDashboardPage />} />,
  },
  { path: "team", element: <ByLook classic={<TeamRollupPage />} next={<TeamBoardPage />} /> },
  // /teams/:id stays the classic team page either way.
  { path: "teams", element: <ByLook classic={<TeamsPage />} next={<AllTeamsPage />} /> },
  { path: "epic-requests", element: ideasPage },
  { path: "epics", element: blueprintsPage },
];
